import React, { useContext, useEffect, useState } from 'react';
import { APIContext } from '../../api';
import { Input, Switch, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure, Table, Thead, Tbody, Tr, Th, Td, Badge, IconButton, Tooltip } from '@chakra-ui/react';
import { PulseLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faCopy } from '@fortawesome/free-solid-svg-icons';

function PlanAddons() {
  const { adminActions } = useContext(APIContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);
  const [addons, setAddons] = useState([]);
  const [editingAddon, setEditingAddon] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    variant_id: '',
    polar_variant_id: '',
    price: '',
    websites_limit: '',
    ai_credits_limit: '',
    storage_limit: '',
    email_limit: '',
    subscribers_limit: '',
    whitelabel: false,
    byok: false,
    webhooks: false,
    mcp: false,
    allow_without_base_subscription: false,
  });

  useEffect(() => {
    loadAddons();
  }, []);

  const loadAddons = async () => {
    try {
      setIsLoading(true);
      const response = await adminActions('admin/plan_addons/list', {});
      if (response && response.Output) {
        setAddons(response.Output);
      } else {
        setAddons([]);
      }
    } catch (error) {
      console.error('Error loading addons:', error);
      toast.error('Failed to load plan addons');
      setAddons([]);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      variant_id: '',
      polar_variant_id: '',
      price: '',
      websites_limit: '',
      ai_credits_limit: '',
      storage_limit: '',
      email_limit: '',
      subscribers_limit: '',
      whitelabel: false,
      byok: false,
      webhooks: false,
      mcp: false,
      allow_without_base_subscription: false,
    });
    setEditingAddon(null);
  };

  const handleOpenModal = (addon = null) => {
    if (addon) {
      setEditingAddon(addon);
      setFormData({
        name: addon.name || '',
        description: addon.description || '',
        variant_id: addon.variant_id || '',
        polar_variant_id: addon.polar_variant_id || '',
        price: addon.price || '',
        websites_limit: addon.websites_limit || '',
        ai_credits_limit: addon.ai_credits_limit || '',
        storage_limit: addon.storage_limit || '',
        email_limit: addon.email_limit || '',
        subscribers_limit: addon.subscribers_limit || '',
        whitelabel: addon.whitelabel || false,
        byok: addon.byok || false,
        webhooks: addon.webhooks || false,
        mcp: addon.mcp || false,
        allow_without_base_subscription: addon.allow_without_base_subscription || false,
      });
    } else {
      resetForm();
    }
    onOpen();
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    if (!formData.variant_id.trim() && !formData.polar_variant_id.trim()) {
      toast.error('Lemon Squeezy Variant ID or Polar Variant ID are required');
      return;
    }

    // Validate numeric fields
    if (formData.price && isNaN(parseFloat(formData.price))) {
      toast.error('Price must be a valid number');
      return;
    }

    if (formData.websites_limit && isNaN(parseInt(formData.websites_limit))) {
      toast.error('Websites limit must be a valid number');
      return;
    }

    if (formData.ai_credits_limit && isNaN(parseInt(formData.ai_credits_limit))) {
      toast.error('AI credits limit must be a valid number');
      return;
    }

    if (formData.storage_limit && isNaN(parseInt(formData.storage_limit))) {
      toast.error('Storage limit must be a valid number');
      return;
    }

    if (formData.email_limit && isNaN(parseInt(formData.email_limit))) {
      toast.error('Email limit must be a valid number');
      return;
    }

    if (formData.subscribers_limit && isNaN(parseInt(formData.subscribers_limit))) {
      toast.error('Subscribers limit must be a valid number');
      return;
    }

    try {
      setIsSubmitting(true);
      const action = editingAddon ? 'admin/plan_addons/update' : 'admin/plan_addons/create';
      const params = editingAddon 
        ? { addon_id: editingAddon.pk.replace('plan-addon-', ''), ...formData }
        : formData;

      await adminActions(action, params);
      
      toast.success(editingAddon ? 'Plan addon updated successfully!' : 'Plan addon created successfully!');
      onClose();
      resetForm();
      loadAddons();
    } catch (error) {
      console.error('Error saving addon:', error);
      toast.error('Failed to save plan addon');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (addon) => {
    if (!window.confirm(`Are you sure you want to delete "${addon.name}"?`)) {
      return;
    }

    try {
      await adminActions('admin/plan_addons/delete', { 
        addon_id: addon.pk.replace('plan-addon-', '') 
      });
      
      toast.success('Plan addon deleted successfully!');
      loadAddons();
    } catch (error) {
      console.error('Error deleting addon:', error);
      toast.error('Failed to delete plan addon');
    }
  };

  const handleArchive = async (addon) => {
    try {
      const action = addon.status === 'active' ? 'admin/plan_addons/archive' : 'admin/plan_addons/activate';
      await adminActions(action, { 
        addon_id: addon.pk.replace('plan-addon-', '') 
      });
      
      toast.success(addon.status === 'active' ? 'Plan addon archived successfully!' : 'Plan addon activated successfully!');
      loadAddons();
    } catch (error) {
      console.error('Error archiving/activating addon:', error);
      toast.error('Failed to update plan addon status');
    }
  };

  const handleCopyId = async (addonId) => {
    try {
      await navigator.clipboard.writeText(addonId?.replace('plan-addon-', ''));
      toast.success('Addon ID copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy addon ID');
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <PulseLoader size={20}></PulseLoader>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-[26px]">Plan Addons Management</h2>
        <Button
          colorScheme="blue"
          onClick={() => handleOpenModal()}
          leftIcon={<FontAwesomeIcon icon={faPlus} />}
        >
          Create New Addon
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {addons.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No plan addons found. Create your first addon to get started.</p>
          </div>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Plan ID</Th>
                <Th>Price</Th>
                <Th>Limits</Th>
                <Th>Features</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {addons.map((addon) => (
              <Tr key={addon.pk}>
                <Td>
                  <div>
                    <div className="font-medium">{addon.name}</div>
                    {addon.description && (
                      <div className="text-sm text-gray-500">{addon.description}</div>
                    )}
                  </div>
                </Td>
                <Td>
                  <div className="flex items-center space-x-2">
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                      {addon.pk?.replace('plan-addon-', '')}
                    </code>
                    <Tooltip label="Copy Addon ID">
                      <IconButton
                        size="xs"
                        icon={<FontAwesomeIcon icon={faCopy} />}
                        onClick={() => handleCopyId(addon.pk)}
                        colorScheme="gray"
                        variant="ghost"
                      />
                    </Tooltip>
                  </div>
                </Td>
                <Td>${addon.price || 0}</Td>
                <Td>
                  <div className="text-sm space-y-1">
                    <div>Websites: {addon.websites_limit || 0}</div>
                    <div>AI Credits: {addon.ai_credits_limit || 0}</div>
                    <div>Storage: {addon.storage_limit || 0}GB</div>
                    <div>Emails: {addon.email_limit || 0}</div>
                    <div>Subscribers: {addon.subscribers_limit || 0}</div>
                  </div>
                </Td>
                <Td>
                  <div className="space-y-1">
                    {addon.whitelabel && <Badge colorScheme="green">Whitelabel</Badge>}
                    {addon.byok && <Badge colorScheme="blue">BYOK</Badge>}
                    {addon.webhooks && <Badge colorScheme="purple">Webhooks</Badge>}
                    {addon.mcp && <Badge colorScheme="orange">MCP</Badge>}
                  </div>
                </Td>
                <Td>
                  <Badge 
                    colorScheme={addon.status === 'active' ? 'green' : 'gray'}
                    onClick={() => handleArchive(addon)}
                    cursor="pointer"
                  >
                    {addon.status || 'active'}
                  </Badge>
                </Td>
                <Td>{formatDate(addon.created_at)}</Td>
                <Td>
                  <div className="flex space-x-2">
                    <Tooltip label="Edit">
                      <IconButton
                        size="sm"
                        icon={<FontAwesomeIcon icon={faEdit} />}
                        onClick={() => handleOpenModal(addon)}
                        colorScheme="blue"
                        variant="ghost"
                      />
                    </Tooltip>
                    <Tooltip label="Delete">
                      <IconButton
                        size="sm"
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() => handleDelete(addon)}
                        colorScheme="red"
                        variant="ghost"
                      />
                    </Tooltip>
                  </div>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingAddon ? 'Edit Plan Addon' : 'Create New Plan Addon'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter addon name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter addon description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Lemon Squeezy Variant ID</label>
                <Input
                  value={formData.variant_id}
                  onChange={(e) => setFormData({ ...formData, variant_id: e.target.value })}
                  placeholder="Enter variant ID"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Polar Variant ID *</label>
                <Input
                  value={formData.polar_variant_id}
                  onChange={(e) => setFormData({ ...formData, polar_variant_id: e.target.value })}
                  placeholder="Enter variant ID"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Price ($)</label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  onWheel={(e) => e.target.blur()}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Websites Limit</label>
                  <Input
                    type="number"
                    value={formData.websites_limit}
                    onChange={(e) => setFormData({ ...formData, websites_limit: e.target.value })}
                    onWheel={(e) => e.target.blur()}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">AI Credits Limit</label>
                  <Input
                    type="number"
                    value={formData.ai_credits_limit}
                    onChange={(e) => setFormData({ ...formData, ai_credits_limit: e.target.value })}
                    onWheel={(e) => e.target.blur()}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Storage Limit (GB)</label>
                  <Input
                    type="number"
                    value={formData.storage_limit}
                    onChange={(e) => setFormData({ ...formData, storage_limit: e.target.value })}
                    onWheel={(e) => e.target.blur()}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email Limit</label>
                  <Input
                    type="number"
                    value={formData.email_limit}
                    onChange={(e) => setFormData({ ...formData, email_limit: e.target.value })}
                    onWheel={(e) => e.target.blur()}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subscribers Limit</label>
                  <Input
                    type="number"
                    value={formData.subscribers_limit}
                    onChange={(e) => setFormData({ ...formData, subscribers_limit: e.target.value })}
                    onWheel={(e) => e.target.blur()}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Whitelabel</label>
                  <Switch
                    isChecked={formData.whitelabel}
                    onChange={(e) => setFormData({ ...formData, whitelabel: e.target.checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">BYOK (Bring Your Own Key)</label>
                  <Switch
                    isChecked={formData.byok}
                    onChange={(e) => setFormData({ ...formData, byok: e.target.checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Webhooks</label>
                  <Switch
                    isChecked={formData.webhooks}
                    onChange={(e) => setFormData({ ...formData, webhooks: e.target.checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">MCP</label>
                  <Switch
                    isChecked={formData.mcp}
                    onChange={(e) => setFormData({ ...formData, mcp: e.target.checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Allow without base subscription</label>
                  <Switch
                    isChecked={formData.allow_without_base_subscription}
                    onChange={(e) => setFormData({ ...formData, allow_without_base_subscription: e.target.checked })}
                  />
                </div>
              </div>
            </div>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText="Saving..."
            >
              {editingAddon ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}

export default PlanAddons; 